import { useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { Separator, Tooltip } from '@blade/ui-kit'
import { CurrencyIcon } from '@root/entities/currency/ui'
import { type PairModel } from '@root/entities/pair'
import { usePairDevTokensInfo } from '@root/entities/pair/hooks/use-pair-dev-tokens-info'
import { PairLink } from '@root/entities/pair/ui/common'
import { CountMax3DigitsFormatted } from '@root/shared/core/format'
import {
  CrownOutlinedIcon,
  ReservedSpace,
  TooltipCard,
  TooltipCardParentGuardProvider,
} from '@root/shared/ui-kit'

import { ExploreDevTokensTooltip } from '../explore-dev-tooltip/explore-dev-tokens-tooltip'

import type { ClassNameProps } from '@root/shared/react'

type ExploreTableAddressCellProps = {
  pair: PairModel
} & ClassNameProps

export const ExploreTableDevTokensCell: React.FC<
  ExploreTableAddressCellProps
> = ({ className, pair }) => {
  const [creatorStats] = useAtom(pair.creatorStats)

  const devTokensRenderData = usePairDevTokensInfo(pair)

  const [firstToken, secondToken, thirdToken] =
    devTokensRenderData?.topTokensWithoutCurrent ?? []

  const topTokens = [firstToken, secondToken, thirdToken].filter(Boolean)

  const restTokensCount = devTokensRenderData?.totalPoolsWithoutCurrent
    ? devTokensRenderData.totalPoolsWithoutCurrent - topTokens.length
    : 0

  const renderBondedPools = () => {
    if (
      devTokensRenderData?.bondedTokensWithoutCurrent !== undefined &&
      devTokensRenderData.bondedTokensWithoutCurrent > 0
    ) {
      return (
        <TooltipCard
          closeArea={
            <ExploreDevTokensTooltip.CloseArea
              devTokensInfo={devTokensRenderData}
            />
          }
          clickArea={
            <ExploreDevTokensTooltip.ClickArea
              devTokensInfo={devTokensRenderData}
            />
          }
        >
          <Count pair={pair}>
            <CountNumber $chars={2}>
              {devTokensRenderData.bondedTokensWithoutCurrent}
            </CountNumber>{' '}
            <CrownOutlinedIcon size={16} />
          </Count>
        </TooltipCard>
      )
    }

    return undefined
  }

  const renderTopTokens = () => {
    if (topTokens.length === 0) {
      return undefined
    }
    return (
      <Tokens>
        {topTokens.map((devToken) => (
          <Token key={`${devToken.address}_${devToken.pairAddress}`}>
            <CurrencyIcon
              withGoogleLens={devToken.pairAddress === undefined}
              withPremiumFallback={false}
              currency={devToken.currencyModel}
              pairAddress={devToken.pairAddress}
              size={16}
              pairSide="top"
            />
          </Token>
        ))}
        {restTokensCount > 0 && (
          <Tooltip content={restTokensCount}>
            <RestTokensCount pair={pair}>
              +<CountMax3DigitsFormatted count={restTokensCount} />
            </RestTokensCount>
          </Tooltip>
        )}
      </Tokens>
    )
  }

  const bondedPoolsEl = renderBondedPools()
  const topTokensEl = renderTopTokens()

  return (
    <Root className={className}>
      {creatorStats ? (
        <TooltipCardParentGuardProvider withCascadePreviews>
          <Content>
            {bondedPoolsEl}

            {bondedPoolsEl !== undefined && topTokensEl !== undefined && (
              <Sep vertical />
            )}

            {topTokensEl}
          </Content>
        </TooltipCardParentGuardProvider>
      ) : undefined}
    </Root>
  )
}

const Root = styled.div`
  padding: 0 4px;
`

const Content = styled.div`
  display: flex;
  flex-wrap: nowrap;

  gap: 3px;
  color: var(--text-11);
  align-items: center;
  font-size: 12px;
  font-weight: 500;
`
const Count = styled(PairLink<PairModel>)`
  pointer-events: auto;
  color: var(--amber-9);
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 2px;
`
const Sep = styled(Separator)`
  height: 18px;
  margin: 0 1px 0 0;
  background: var(--backgroundA-5);
`
const Tokens = styled.div`
  display: flex;
  gap: 4px;
  color: var(--text-11);
  align-items: center;
  flex-wrap: nowrap;
`
const Token = styled.div`
  pointer-events: auto;
`
const RestTokensCount = styled(PairLink<PairModel>)`
  pointer-events: auto;
  color: var(--text-8);
  height: 16px;
  min-width: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const CountNumber = styled(ReservedSpace)`
  text-align: right;
`
